import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { ModelType } from '~/anonymizer/anonymizer';
import { RequestParameters } from '~/backend/backend-client';
import { Modality } from '~/domain/dicom/modality';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { environmentState } from '~/state/atoms/engineAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { transformDoseTargets } from '~/utils/transforms/doseTargetsTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import useProcessDicomFiles from '../../../useProcessDicomFiles';
import useViewportEnvironment from '../../../useViewportEnvironment';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useDoseBackend, useSessionID } from '../useBackend';

/**
 * A React hook for creating a dose prediction task.
 * @returns {Object} - An object containing startProcessing function and submission state.
 */
export function useDosePrediction() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const primaryImage = useRecoilValue(primaryImageState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const environment = useRecoilValue(environmentState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const dosePredictionTask = useRecoilValue(dosePredictionTaskState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const doseClient = useDoseBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentDose } = useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const finalFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(job.results, ModelType.Dose)
                : job.results;
            if (finalFiles) {
                const generationModel = transformTaskToGenerationModel(
                    job.task
                );
                const processedItems = await processCreatedFiles(
                    finalFiles,
                    generationModel
                );
                if (processedItems) {
                    updateJob(job.id, { isImported: true });
                    setEnvironmentDose(
                        processedItems.doses,
                        selectedPatientId ?? ''
                    );
                }
            } else {
                handleError('Re-identification failed for one or more files.');
            }
        }
    };

    const startProcessing = async (modelName: string) => {
        if (isSubmitting) {
            return;
        }

        if (primaryImage && currentStructureSet && environment) {
            if (
                primaryImage.seriesMetadata.modality !== Modality.CT ||
                primaryImage.firstSlice.tilted ||
                primaryImage.firstSlice.patientPosition !== 'HFS'
            ) {
                handleError(
                    new Error(),
                    'The selected image is not in the correct orientation. Please select a CT image in HFS position.'
                );
                return;
            }

            try {
                setIsSubmitting(true);
                resetSessionID();

                const blobs = Array.from(primaryImage.getSlicesAsDicomBlobs());
                environment.saveChanges();
                blobs.push(currentStructureSet.export());
                const blobsToUpload = pseudonymize
                    ? await anonymizeBlobs(blobs, ModelType.Dose)
                    : blobs;

                const backendParameters: RequestParameters = {
                    action: modelName,
                    taskUID: `${sessionID}.${v4().split('-')[0]}`,
                    nExpectedFiles: blobs.length.toString(),
                };

                if (
                    dosePredictionTask?.targets &&
                    dosePredictionTask.targets.length > 0
                ) {
                    backendParameters.targets = JSON.stringify({
                        unit: 'Gy',
                        rois: transformDoseTargets(dosePredictionTask.targets),
                    });
                }

                if (blobsToUpload) {
                    const newJobDetails: NewJobData = {
                        id: sessionID,
                        name: 'Dose+',
                        totalItems: blobsToUpload.length,
                        type: WorkflowTypes.DOSE,
                        client: doseClient,
                        onLoad: handleProcessedFiles,
                        payload: {
                            filesToUpload: blobsToUpload.map((file) => ({
                                file,
                            })),
                            backendParameters,
                            pseudonymized: pseudonymize,
                        },
                        task: {
                            model: dosePredictionTask.model,
                            targets: dosePredictionTask.targets,
                        },
                    };

                    addJob(newJobDetails);
                    setWorkflowModalState((state) => ({
                        ...state,
                        open: false,
                    }));
                } else {
                    handleError('Anonymization failed for one or more files.');
                }
            } catch (error) {
                handleError(error);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            handleError('Image or structure set is missing!');
        }
    };

    return { startProcessing, isSubmitting };
}
